// services/v3/datasource/index.ts — factory + readonly wrapper (SPEC §4.1)
import type {
  DataSource, DataSourceContext, EntityVersions, Unsubscribe, Versioned,
} from '~/types/v3/datasource'
import type {
  ChannelPriceMap, ProductAllowMap, ProductsPayload, SettingsPayload,
  SoldoutMap, StoreProfile, TableDoc, TableState,
} from '~/types/v3/entities'
import type { QrToken } from '~/types/v3/token'
import type { QrJobCard, SubmitResult } from '~/types/v3/job'
import { readV3Config, type DataSourceKind, type V3Config } from '../config'
import { isReadonly } from '../writeGuard'
import { RtdbSource } from './RtdbSource'
import { FirestoreSource } from './FirestoreSource'
import { MockSource } from './MockSource'

const SIMULATED_SUBMIT_DELAY_MS = 300

/**
 * Delegates everything to the inner source, except `submitOrder`, which is
 * short-circuited (never reaches the backend) while the app runs read-only.
 */
class ReadonlyGuardedSource implements DataSource {
  constructor(private readonly inner: DataSource) {}

  get kind(): DataSource['kind'] { return this.inner.kind }
  get supportsVersions(): boolean { return this.inner.supportsVersions }

  init(ctx: DataSourceContext): Promise<void> { return this.inner.init(ctx) }
  dispose(): void { this.inner.dispose() }
  getVersions(): Promise<EntityVersions | null> { return this.inner.getVersions() }
  watchVersions(cb: (v: EntityVersions) => void): Unsubscribe { return this.inner.watchVersions(cb) }
  fetchSettings(): Promise<Versioned<SettingsPayload>> { return this.inner.fetchSettings() }
  fetchProducts(): Promise<Versioned<ProductsPayload>> { return this.inner.fetchProducts() }
  fetchChannelPrice(): Promise<Versioned<ChannelPriceMap>> { return this.inner.fetchChannelPrice() }
  fetchMenuSet(settingId: string): Promise<Versioned<ProductAllowMap>> { return this.inner.fetchMenuSet(settingId) }
  getStoreProfile(): Promise<StoreProfile | null> { return this.inner.getStoreProfile() }
  watchSoldout(cb: (m: SoldoutMap) => void): Unsubscribe { return this.inner.watchSoldout(cb) }
  watchTableState(table: string, cb: (s: TableState | null) => void): Unsubscribe { return this.inner.watchTableState(table, cb) }
  watchTableItems(table: string, cb: (d: TableDoc | null) => void): Unsubscribe { return this.inner.watchTableItems(table, cb) }
  watchQrcode(qrcodeId: string, cb: (t: QrToken | null) => void): Unsubscribe { return this.inner.watchQrcode(qrcodeId, cb) }
  getQrcode(qrcodeId: string): Promise<QrToken | null> { return this.inner.getQrcode(qrcodeId) }
  getTableState(table: string): Promise<TableState | null> { return this.inner.getTableState(table) }
  getServerNowISO(): Promise<string> { return this.inner.getServerNowISO() }

  submitOrder(job: QrJobCard): Promise<SubmitResult> {
    if (!isReadonly()) return this.inner.submitOrder(job)
    console.info('[readonly] submitOrder', job)
    return new Promise<SubmitResult>((resolve) => {
      setTimeout(
        () => resolve({ ok: true, jobId: job.jobId, simulated: true, alreadyExisted: false }),
        SIMULATED_SUBMIT_DELAY_MS,
      )
    })
  }
}

function instantiate(kind: DataSourceKind): DataSource {
  switch (kind) {
    case 'mock': return new MockSource()
    case 'firestore': return new FirestoreSource()
    case 'rtdb':
    default: return new RtdbSource()
  }
}

let current: { kind: DataSourceKind; source: DataSource } | null = null

/** Memoised singleton per `kind`; a different kind disposes the previous source. */
export function createDataSource(cfg?: V3Config): DataSource {
  const kind = (cfg ?? readV3Config()).dataSource
  if (current && current.kind === kind) return current.source
  if (current) disposeDataSource()
  const inner = instantiate(kind)
  // The mock backend IS the simulation (it records jobs on `window.__mockOrders`
  // and never touches Firebase), so wrapping it would hide every submit from the
  // e2e suite, which runs with `writeMode=readonly`. Only real backends are guarded.
  current = { kind, source: kind === 'mock' ? inner : new ReadonlyGuardedSource(inner) }
  return current.source
}

/** Disposes and forgets the memoised source. */
export function disposeDataSource(): void {
  const held = current
  current = null
  if (!held) return
  try { held.source.dispose() } catch (e) { console.warn('[v3] dispose failed', e) }
}

/** The memoised source, if one exists (no instantiation). */
export function peekDataSource(): DataSource | null {
  return current?.source ?? null
}
